import isSSR from '@/utils/isSSR';
import { ref, useSSRContext, type Ref } from 'vue';
import type { SSRContext } from '@/types/ssr-render.type';
import { useRouter } from 'vue-router';
import type { LocationQueryValue, RouteParamsGeneric } from 'vue-router';

export interface RouteLike {
  path: string;
  query: Record<string, LocationQueryValue | LocationQueryValue[]>;
  params: RouteParamsGeneric;
  fullPath: string;
}

/**
 * Function ensures that a route param that is expected to be
 * a string is a string and not a string array.
 * In case it is an array it returns the first string from the array.
 */
export const getRouteParamAsString = (value: string | string[] | undefined): string | undefined => {
  if (value === undefined) {
    return undefined;
  }

  const val = Array.isArray(value) ? value[0] : value;
  return val.toLowerCase();
};

/**
 * A safer option for getting route data. Vue Router's route object can get messed up across requests on high load.
 * This should be used everywhere instead of useRoute, $route or useRouter().currentRoute to avoid hard-to-debug bugs on the server side.
 * Other Router functionality can be used normally. Just the current route object gets mixed between requests.
 * @returns
 * - On SSR, a custom route object as a ref created from the request url and route params immediately after the router has been set up.
 * - On the client, returns a normal reactive route object from the Vue Router. *
 */
export default function useSafeRoute(): Ref<RouteLike> {
  if (isSSR) {
    const ctx = useSSRContext<SSRContext>();
    if (ctx === undefined) {
      throw new Error('SSR context missing');
    } else if (!ctx.route) {
      throw new Error('SSR context missing route');
    }

    return ref(ctx.route);
  } else {
    return useRouter().currentRoute;
  }
}
